import { FileText, Hand, Play } from "lucide-react";
import { useState } from "react";
import { DOC_TYPE_LABELS, pageImageUrl } from "../../lib/api";
import type { Me } from "../../lib/me";
import { RoleButton } from "../RequireRole";
import { RefusalNotice } from "./StartForm";
import { displayName, percent } from "../../lib/words";
import {
  decline, documentsDecision, fieldHeading, fieldWord, gateOf, plainReason, resumeDecision, reviewDecision, specDecision, stopHeadline,
  type Decided, type Decision, type Refusal, type RunView,
} from "./moduleFlow";
import { TableScroll } from "../TableScroll";

interface GateProps {
  me: Me | null | undefined;
  busy: boolean;
  onDecide: (decision: Decision) => void;
  onDecline: () => void;
}

export interface Candidate {
  path: string;
  file_name?: string;
  doc_type?: string;
  resolution?: string;
  confidence?: number;
  found_on_pages?: number[];
  title?: string | null;
  vendor?: string | null;
  recommended?: boolean;
}

const FOUND_BY: Record<string, string> = {
  index_and_content: "Found in the index and in the document",
  index_row: "Found in the master index",
  content_scan: "Found by reading the document",
};

function Problem({ text }: { text: string | null }) {
  return text ? <p role="alert" style={{ color: "var(--color-fail)", fontSize: 14 }}>{text}</p> : null;
}

function useDecided(onDecide: (d: Decision) => void) {
  const [problem, setProblem] = useState<string | null>(null);
  const send = (decided: Decided) => {
    if ("problem" in decided) return setProblem(decided.problem);
    setProblem(null);
    onDecide(decided.decision);
  };
  return { problem, send };
}

function Actions({ me, busy, primary, onDecline, declineLabel = "Decline" }: {
  me: Me | null | undefined; busy: boolean; primary: string; onDecline: () => void; declineLabel?: string;
}) {
  return (
    <div className="row" style={{ flexWrap: "wrap" }}>
      <RoleButton role="engineer" me={me} type="submit" disabled={busy}>{busy ? "Sending" : primary}</RoleButton>
      <RoleButton role="engineer" me={me} type="button" className="ghost" disabled={busy} onClick={onDecline}>{declineLabel}</RoleButton>
    </div>
  );
}

/** Which documents to read for a tag. The recommended ones start ticked. */
export function DocumentPicker({ candidates, me, busy, onDecide, onDecline }: GateProps & { candidates: Candidate[] }) {
  const [chosen, setChosen] = useState<string[]>(() => candidates.filter((c) => c.recommended).map((c) => c.path));
  const { problem, send } = useDecided(onDecide);
  if (candidates.length === 0) {
    return (
      <div className="stack">
        <p className="muted">No document names this tag, so there is nothing to read. Decline to close the run.</p>
        <div><RoleButton role="engineer" me={me} className="ghost" disabled={busy} onClick={onDecline}>Decline</RoleButton></div>
      </div>
    );
  }
  const toggle = (path: string) =>
    setChosen((c) => (c.includes(path) ? c.filter((p) => p !== path) : [...c, path]));
  return (
    <form className="stack" style={{ gap: 10 }} onSubmit={(e) => { e.preventDefault(); send(documentsDecision(chosen)); }}>
      <p className="muted" style={{ fontSize: 13 }}>Each document read is one paid page read or model call. Pick only what is needed.</p>
      {candidates.map((c) => {
        const first = c.found_on_pages?.[0] ?? 1;
        return (
          <label key={c.path} className="doc-pick">
            <input type="checkbox" value={c.path} checked={chosen.includes(c.path)} onChange={() => toggle(c.path)} />
            <span className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
              <span className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                <FileText size={14} aria-hidden="true" />
                <strong className="mono" style={{ wordBreak: "break-all" }}>{c.file_name ? displayName(c.file_name) : displayName(c.path)}</strong>
                {c.recommended && <span className="pill ok">Recommended</span>}
              </span>
              {c.title && <span style={{ fontSize: 14 }}>{displayName(c.title)}{c.vendor ? `, ${c.vendor}` : ""}</span>}
              <span className="muted" style={{ fontSize: 13 }}>
                {FOUND_BY[c.resolution ?? ""] ?? c.resolution}
                {c.doc_type ? `. ${DOC_TYPE_LABELS[c.doc_type] ?? c.doc_type.replace(/_/g, " ")}` : ""}
                {c.found_on_pages?.length ? `. Tag on page ${c.found_on_pages.join(", ")}` : ""}
              </span>
              <a href={pageImageUrl(c.path, first)} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
                View page {first}
              </a>
            </span>
          </label>
        );
      })}
      <Problem text={problem} />
      <Actions me={me} busy={busy} primary={`Read ${chosen.length} document${chosen.length === 1 ? "" : "s"}`} onDecline={onDecline} />
    </form>
  );
}

/** The identity a lifecycle search will use. A value can be corrected; a disputed one must be. */
export function SpecApproval({ spec, unresolved, me, busy, onDecide, onDecline }: GateProps & {
  spec: Record<string, string>; unresolved: string[];
}) {
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const { problem, send } = useDecided(onDecide);
  const fields = Object.keys(spec);
  return (
    <form className="stack" style={{ gap: 10 }} onSubmit={(e) => { e.preventDefault(); send(specDecision(unresolved, overrides)); }}>
      {unresolved.length > 0 && (
        <p style={{ fontSize: 14 }} role="note">
          Two documents disagree on the {unresolved.map(fieldWord).join(", ")}. Type the value to search on.
        </p>
      )}
      <TableScroll label="Specification to search on">
      <table>
        <thead><tr><th>Field</th><th>Value read</th><th>Search on instead</th></tr></thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f}>
              <td>{fieldHeading(f)}{unresolved.includes(f) && <span className="pill review" style={{ marginLeft: 8 }}>disputed</span>}</td>
              <td className="mono">{String(spec[f] ?? "")}</td>
              <td>
                <input aria-label={`Value to search on for the ${fieldWord(f)}`} value={overrides[f] ?? ""}
                       onChange={(e) => setOverrides((o) => ({ ...o, [f]: e.target.value }))}
                       placeholder={unresolved.includes(f) ? "required" : "keep"} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </TableScroll>
      <Problem text={problem} />
      <Actions me={me} busy={busy} primary="Approve and search" onDecline={onDecline} />
    </form>
  );
}

interface ReviewPrompt {
  tag?: string;
  reasons?: string[];
  equipment_class?: string | null;
  asset_class?: string | null;
  asset?: { fields?: { name?: string; value?: unknown; confidence?: number }[] } | null;
}

/** A package run stopped on one tag. Approving lets it research that tag; skipping moves on. */
export function IdentityReview({ prompt, me, busy, onDecide, onDecline }: GateProps & { prompt: ReviewPrompt }) {
  const tag = prompt.tag ?? "";
  const { problem, send } = useDecided(onDecide);
  const fields = (prompt.asset?.fields ?? []).filter((f) => f.name);
  return (
    <form className="stack" style={{ gap: 10 }} onSubmit={(e) => { e.preventDefault(); send(reviewDecision(tag, {})); }}>
      <p style={{ fontSize: 15 }}><strong>{stopHeadline(prompt)}</strong>.</p>
      {(prompt.reasons ?? []).length > 1 && (
        <ul style={{ paddingLeft: 20, fontSize: 14 }}>
          {prompt.reasons!.slice(1).map((r) => <li key={r}>Also, {plainReason(r)}.</li>)}
        </ul>
      )}
      {fields.length > 0 && (
        <TableScroll label="Fields read for this tag">
        <table>
          <thead><tr><th>Field</th><th>Value</th><th style={{ textAlign: "right" }}>Confidence</th></tr></thead>
          <tbody>
            {fields.map((f) => (
              <tr key={f.name}>
                <td>{fieldHeading(f.name!)}</td>
                <td className="mono">{f.value == null || f.value === "" ? "not found" : String(f.value)}</td>
                <td className="mono" style={{ textAlign: "right" }}>{percent(f.confidence)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroll>
      )}
      <Problem text={problem} />
      <Actions me={me} busy={busy} primary={`Approve ${tag}`} onDecline={onDecline} declineLabel={`Skip ${tag}`} />
    </form>
  );
}

/** Whatever the run is waiting on, or a Resume for a run a restart interrupted with no gate open. */
export function GatePanel({ run, me, busy, refused, onDecide }: {
  run: RunView; me: Me | null | undefined; busy: boolean; refused: Refusal | null; onDecide: (d: Decision) => void;
}) {
  const gate = gateOf(run);
  const prompt = (run.gate_prompt ?? {}) as Record<string, unknown>;
  const props = { me, busy, onDecide, onDecline: () => onDecide(decline(run)) };
  let body: React.ReactNode = null;
  if (gate === "confirm_documents") {
    body = <DocumentPicker {...props} candidates={(prompt.candidates as Candidate[] | undefined) ?? []} />;
  } else if (gate === "approve_spec") {
    body = <SpecApproval {...props} spec={(prompt.spec as Record<string, string>) ?? {}} unresolved={(prompt.unresolved as string[]) ?? []} />;
  } else if (gate === "identity_needs_review") {
    body = <IdentityReview {...props} prompt={prompt as ReviewPrompt} />;
  } else if (run.status === "interrupted") {
    body = (
      <div className="stack">
        <p className="muted">The server restarted while this run was working. Resuming carries on from where it stopped.</p>
        <div>
          <RoleButton role="engineer" me={me} disabled={busy} onClick={() => onDecide(resumeDecision())}>
            <span className="row" style={{ gap: 6 }}><Play size={14} aria-hidden="true" />Resume</span>
          </RoleButton>
        </div>
      </div>
    );
  }
  if (!body) return null;
  return (
    <section className="card stack" aria-label="Decision">
      <div className="section-title" style={{ marginBottom: 0 }}>
        <Hand size={20} aria-hidden="true" />
        <h3>{typeof prompt.question === "string" && gate ? prompt.question : gate ? "This run needs a decision" : "Resume the run"}</h3>
      </div>
      {refused && <RefusalNotice refusal={refused} />}
      {body}
    </section>
  );
}
